import React, { useState } from 'react'
import axios from 'axios'
import moment from 'moment'
import { Button, Space, notification, Dropdown, Menu, Modal, message} from 'antd';
import { UndoOutlined, DownloadOutlined, LayoutOutlined } from "@ant-design/icons"
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";
import { orderTrackingNos } from "../../helpers/orderTracking";

const OrderButtons = ({selectedRowKeys, onRefunded, filters, orders}) => {
  const { permissions } = usePermissions();
  const [isDownloading, setIsDownloading] = useState(false)
  const [isRefunding, setIsRefunding] = useState(false)

  const downloadCSV = async () => {
    setIsDownloading(true)

    const hasSelection = selectedRowKeys?.length > 0
    const count = hasSelection ? selectedRowKeys.length : filters.pagination.totalSize

    const messageContent = hasSelection ? (
      <p>
        Are you sure you want to download the {count} selected orders only?<br/>
        To download all {filters.pagination.totalSize} orders matching the current filters, clear the selection first.
      </p>
    ) : (
      <p>
        Are you sure you want to download all {count} orders matching the current filters?<br/>
      </p>
    )

    Modal.confirm({
      content: messageContent,
      title: `Download Order List ?`,
      async onOk() {
        const params = { ...filters }
        delete params.pagination
        if (hasSelection) {
          params["orderId[]"] = selectedRowKeys
        }
        await axios
          .get("api/allOrder/csv", { params }).then(res => {
            message.info(`Please check the Export progress in System -> Jobs`)
          }).catch(error => {
            message.error(error.message)
          })
      },
    });

    setIsDownloading(false)
  }

  const refundOrders = orderIds => {
    setIsRefunding(true)

    const allRefunds = Promise.all(
      orderIds.map(orderId => axios.patch("api/order/refund", { orderId }))
    );

    allRefunds
      .then(() => onRefunded())
      .catch(() => {
        console.log("Some refunds didn't work");
      });

    setIsRefunding(false)
  }

  const bulkDownload = async(e) => {
    setIsDownloading(true)

    const a4 = e.key !== "thermal";
    const selectedOrders = orders.filter(order =>
      selectedRowKeys.includes(order.orderId)
    );

    const ordersBySP = {};
    let skippedCount = 0;

    for (const order of selectedOrders) {
      const trackingNos = orderTrackingNos(order);
      if (trackingNos.length === 0) {
        skippedCount += 1;
        continue;
      }
      const sp = order.serviceProvider;
      if (!ordersBySP[sp]) ordersBySP[sp] = [];
      ordersBySP[sp].push(...trackingNos);
    }

    if (skippedCount > 0) {
      notification.warning({
        message: `${skippedCount} order(s) skipped`,
        description: "These orders have no consignment note to download yet.",
      });
    }

    const downloadPromises = Object.entries(ordersBySP).map(
      async ([serviceProvider, trackingNo]) => {

        const date = moment().utcOffset("+0800").format("DD-MM-YYYY");

        return axios
          .post(
            `/api/consign-note/bulk-download`,
            { trackingNo, a4 },
            { responseType: "blob" }
          )
          .then((response) => {
            const blob = new Blob([response.data], { type: "application/pdf" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `${serviceProvider}_${date}.pdf`;
            link.click();
            URL.revokeObjectURL(link.href);
          })
          .catch((error) => {
            notification.error({
              message: `Error downloading notes for ${serviceProvider}`,
              description: error.response?.data.message || error.message,
            });
          });
      }
    );

    await Promise.all(downloadPromises);
    setIsDownloading(false)
  }

  const downloadMenu = (
    <Menu onClick={e => bulkDownload(e)}>
      <Menu.Item key="a4" icon={<LayoutOutlined />}>A4</Menu.Item>
      <Menu.Item key="thermal" icon={<LayoutOutlined />}>Thermal</Menu.Item>
    </Menu>
  );

  return (
    <div style={{ marginBottom: 16, float: "right" }}>
      <Space>
        <span color="blue">
        {permissions?.includes(PERMISSIONS.BULK_REFUND) && onRefunded && (
            <Button
              onClick={() => refundOrders(selectedRowKeys)}
              loading={isRefunding}
              disabled={selectedRowKeys.length === 0}
            >
              <UndoOutlined style={{ fontSize: "20px" }} /> Refund{" "}
            </Button>
          )}
        </span>

        <span>
        {permissions?.includes(PERMISSIONS.BULK_DOWNLOAD_CONNOTE) && (
            <Dropdown
              overlay={downloadMenu}
              disabled={isDownloading || selectedRowKeys.length === 0}
            >
              <Button>
                <Space>
                  {isDownloading ? "Downloading" : "Connote"}
                  <DownloadOutlined style={{ fontSize: "20px" }} />
                </Space>
              </Button>
            </Dropdown>
          )}
        </span>

        <span>
          {permissions?.includes(PERMISSIONS.EXPORT_ORDER_LIST) &&
            <Button onClick={downloadCSV} loading={isDownloading}>
              {selectedRowKeys?.length > 0
                ? `Download CSV (${selectedRowKeys.length} Selected)`
                : "Download CSV (All)"}{" "}
              <DownloadOutlined />
            </Button>
          }
        </span>
      </Space>
    </div>
  )
}

export default OrderButtons